/**
 * Parent/child links from the API may use numeric ids in one place and strings in another.
 * Strict Array.prototype.includes / === breaks those pairs (e.g. ['27719'].includes(27719) is false).
 */
export const isTriggeredByFormDefinition = (childFormDefinition, parentFormDefinitionId) =>
  Array.isArray(childFormDefinition.triggeredByFormDefinitionIds) &&
  childFormDefinition.triggeredByFormDefinitionIds.some(tbId => tbId == parentFormDefinitionId);

const findNodeById = (roots, id) => {
  for (const n of roots || []) {
    if (n.formDefinitionId == id) return n;
    const found = n.children?.length ? findNodeById(n.children, id) : null;
    if (found) return found;
  }
  return null;
};

const isInForest = (roots, id) => !!findNodeById(roots, id);

/**
 * Builds nested `children` under each required root, then links any answered form defs that
 * were still not in the tree (e.g. trigger metadata mismatch) onto their trigger parent, or
 * surfaces them as roots so QA can see/edit saved values.
 */
export function buildQaNestedFormDefinitionForest(filteredFormDefinitions) {
  const parentFormDefinitions = filteredFormDefinitions.filter(fd => fd.required);
  let alreadyDisplayed = [...parentFormDefinitions];

  const assignChildrenFormDefinitions = formDefinition => {
    formDefinition.children = filteredFormDefinitions.filter(
      fd =>
        isTriggeredByFormDefinition(fd, formDefinition.formDefinitionId) &&
        !alreadyDisplayed.some(ad => ad.formDefinitionId == fd.formDefinitionId),
    );
    alreadyDisplayed = alreadyDisplayed.concat(formDefinition.children);
    formDefinition.children.forEach(child => assignChildrenFormDefinitions(child));
  };

  parentFormDefinitions.forEach(fd => assignChildrenFormDefinitions(fd));

  for (const fd of filteredFormDefinitions) {
    const hasAnswers = (fd.answers?.length ?? 0) > 0;
    if (!hasAnswers || isInForest(parentFormDefinitions, fd.formDefinitionId)) continue;

    const triggers = fd.triggeredByFormDefinitionIds;

    if (!Array.isArray(triggers) || triggers.length === 0) {
      parentFormDefinitions.push(fd);
      alreadyDisplayed = alreadyDisplayed.concat([fd]);
      assignChildrenFormDefinitions(fd);
      continue;
    }

    let linked = false;
    for (const pid of triggers) {
      const parent = findNodeById(parentFormDefinitions, pid);
      if (!parent) continue;

      parent.children = parent.children ?? [];
      if (parent.children.some(c => c.formDefinitionId == fd.formDefinitionId)) {
        linked = true;
        break;
      }
      parent.children.push(fd);
      alreadyDisplayed = alreadyDisplayed.concat([fd]);
      assignChildrenFormDefinitions(fd);
      linked = true;
      break;
    }

    if (!linked) {
      parentFormDefinitions.push(fd);
      alreadyDisplayed = alreadyDisplayed.concat([fd]);
      assignChildrenFormDefinitions(fd);
    }
  }

  return parentFormDefinitions;
}
